// frontend/src/components/reports/marcasReport.ts
//
// Con qué clave se guarda la marca de fila de un report.
//
// Estaba indexada por `ordenBloque`, que es la columna del ORDER BY de la vista:
// un ORDINAL DE POSICIÓN, no una identidad. Mientras la vista no cambiaba parecía
// estable — el comentario que lo sostenía afirmaba que `block.key` «ya identifica
// el bloque en el negocio», y no lo identificaba. Al renumerar `orden_bloque` la
// vista del terminado, de 52 a 77 cabeceras por pasar el grano a artículo ×
// pedido de venta, las marcas del usuario se quedaban en las claves viejas y
// reaparecían sobre filas distintas: sin error, sin hueco y con aspecto correcto.
// Y sobreviven al refresco y a la recarga, porque el almacén persiste en
// `sessionStorage`.
//
// Es exactamente lo que `claveFila` ya advertía —«nunca su índice, que cambia al
// ordenar, filtrar o paginar»—, sólo que el índice venía de la vista y no de un
// `map`, que es lo que lo hacía difícil de ver.
//
// Así que la clave se compone de los campos que identifican la fila en el
// NEGOCIO, declarados en el registro (`blockKeyFields` / `detailKeyFields`). Una
// marca puede PERDERSE si el dato que la identificaba desaparece del informe, y
// eso está bien: es una anotación de sesión. Lo que no puede es mudarse a otra
// fila.

import type { ReportRow } from '@/types'

import { claveFila } from '@/utils/marcasFilas'
import type { ReportBlock } from '@/utils/reportBlocks'

import { campo } from './formato'
import type { ReportDef } from './reportDefs'

/**
 * Prefijo de la clave de reserva, para cuando ningún campo declarado trae valor.
 *
 * No puede colisionar con una clave de negocio: ningún campo de estas vistas
 * empieza por `#`, y la de reserva tiene además otro número de partes.
 */
const RESERVA = '#pos'

/** ¿Alguna de las partes trae contenido? Un bloque entero vacío no identifica nada. */
function hayValor(partes: (string | null)[]): boolean {
  return partes.some(p => p != null && p !== '')
}

/** Los campos pedidos, leídos de la fila y normalizados a texto o vacío. */
function partes(row: unknown, campos: string[]): (string | null)[] {
  return campos.map(c => {
    const v = campo(row, c)
    return v == null ? '' : String(v)
  })
}

/**
 * Clave de la marca de una CABECERA de bloque.
 *
 * `ordenBloque` sigue entrando, pero sólo como reserva y sólo cuando el bloque no
 * trae ninguno de sus campos de identidad — un caso que no debería existir y que
 * es mejor marcar de forma inestable que no poder marcar.
 */
export function claveBloqueReport(
  def: ReportDef,
  header: unknown,
  ordenBloque: number,
): string {
  const ps = partes(header, def.blockKeyFields)
  return hayValor(ps) ? claveFila(...ps) : claveFila(RESERVA, ordenBloque)
}

/**
 * Clave de la marca de una SUBLÍNEA. Empieza siempre por la de su bloque, así que
 * la factura 3 de un pedido no puede salir marcada dentro de otro.
 *
 * El `tipoFila` va dentro de la clave a propósito, y no sólo para elegir campos:
 * sin él, una factura y una orden de producción de la misma posición compartirían
 * clave, que es lo que pasaba cuando la clave era el índice del array.
 */
export function claveDetalleReport(
  def: ReportDef,
  claveBloque: string,
  row: unknown,
  indice: number,
): string {
  const tipo = String(campo(row, 'tipoFila') ?? '')
  const campos = def.detailKeyFields?.[tipo] ?? []
  const ps = partes(row, campos)
  return hayValor(ps)
    ? claveFila(claveBloque, tipo, ...ps)
    : claveFila(claveBloque, tipo, RESERVA, indice)
}

/**
 * Claves de bloque que se repiten, para el centinela de contrato.
 *
 * Dos bloques con la misma clave comparten marca: marcar uno enciende el otro.
 * No rompe la pantalla y por eso hace falta avisar — es de la misma familia que
 * `nivelesDesconocidos`. Devuelve las claves duplicadas, no los bloques.
 */
export function clavesBloqueDuplicadas<T extends ReportRow>(
  def: ReportDef,
  blocks: ReportBlock<T>[],
): string[] {
  const vistas = new Set<string>()
  const repetidas = new Set<string>()
  for (const b of blocks) {
    const clave = claveBloqueReport(def, b.header, b.key)
    if (vistas.has(clave)) repetidas.add(clave)
    else vistas.add(clave)
  }
  return [...repetidas]
}
